import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  DeliveryService,
  DeliverySummary,
  NO_DELIVERY,
  deliveredShare,
  formatShare,
  noReceiptPhrase,
  receiptsStillDue,
} from '../../../services/message/delivery.service';
import { formatCount } from '../../../../utils/sms';

type Verdict = 'delivered' | 'partial' | 'failed' | 'sending' | 'unconfirmed';

/**
 * Did the message get through? A one-word verdict first, then a thin bar and
 * the counts behind it. Colour only marks failures, so a column of these
 * reads as quiet ink with red exactly where something needs attention.
 * While receipts are still arriving the counts refresh on their own
 * (DeliveryService.live), so a send can be watched as it lands.
 */
@Component({
  selector: 'bk-delivery',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './delivery.component.html',
  styleUrl: './delivery.component.css',
})
export class DeliveryComponent implements OnChanges, OnDestroy {
  private delivery = inject(DeliveryService);
  private cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  /** Message uuid — the reports are keyed by it. */
  @Input() uuid: string | null | undefined;
  /** Message status; only sent messages (completed/active) have receipts. */
  @Input() status: string | null | undefined;
  /**
   * When the message went out. Past the receipt window, recipients without a
   * receipt are reported as unconfirmed rather than still delivering.
   */
  @Input() sentAt: Date | string | null | undefined;

  state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  summary: DeliverySummary = NO_DELIVERY;

  readonly formatCount = formatCount;

  get sent(): boolean {
    return /^(completed|active)$/i.test(this.status ?? '');
  }

  /** Still sending, or recent enough that missing receipts may yet arrive. */
  private get receiptsDue(): boolean {
    return receiptsStillDue(this.status, this.sentAt);
  }

  get verdict(): Verdict {
    const { delivered, failed, inFlight } = this.summary;
    if (inFlight > 0 && this.receiptsDue) return 'sending';
    if (failed > 0 && delivered === 0) return 'failed';
    if (failed > 0) return 'partial';
    if (inFlight > 0) return 'unconfirmed';
    return 'delivered';
  }

  get verdictLabel(): string {
    const share = formatShare(deliveredShare(this.summary));
    switch (this.verdict) {
      case 'failed':
        return 'Failed';
      case 'partial':
        return `${formatCount(this.summary.failed)} failed`;
      case 'sending':
        return `Delivering · ${share}`;
      case 'unconfirmed':
        return `${share} confirmed`;
      default:
        return 'Delivered';
    }
  }

  /** Full breakdown for the tooltip and screen readers. */
  get breakdown(): string {
    const { total, delivered, failed, inFlight } = this.summary;
    const parts = [`${formatCount(delivered)} delivered`];
    if (failed) parts.push(`${formatCount(failed)} failed`);
    if (inFlight) parts.push(noReceiptPhrase(this.summary, this.receiptsDue));
    const live = this.verdict === 'sending' ? '. Updates live' : '';
    return `${parts.join(', ')}, of ${formatCount(total)} recipients${live}`;
  }

  ngOnChanges() {
    this.sub?.unsubscribe();

    if (!this.sent || !this.uuid) {
      this.state = 'idle';
      return;
    }

    this.state = 'loading';
    this.sub = this.delivery.live(this.uuid, (summary) => summary.inFlight > 0 && this.receiptsDue).subscribe({
      next: (summary) => {
        this.summary = summary;
        this.state = 'ready';
        this.cdr.markForCheck();
      },
      error: () => {
        this.state = 'error';
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
